import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap, Trophy, Repeat, Users, Coins } from 'lucide-react';

import type { QduelsTournamentSummary } from '@/types/qduels';
import { cn } from '@/lib/cn';

/**
 * A QDuels tournament row.
 *
 * Deliberately a separate component from the Laravel `<TournamentCard />`: the
 * two models share almost nothing (different ids, statuses, seat semantics, and
 * no image on the QDuels side), so one component serving both would be a union
 * of two disjoint shapes. In the fused main tab they render side by side, told
 * apart by the source badge.
 */
interface Props {
  tournament: QduelsTournamentSummary;
  /** Show the [DUELS] source badge — only needed where the list is mixed. */
  showSourceBadge?: boolean;
  className?: string;
}

export function QduelsTournamentCard({ tournament, showSourceBadge = false, className }: Props) {
  const { t } = useTranslation('duels');
  const isSitngo = tournament.kind === 'sitngo';

  // Sit&Go fills to an EXACT size and auto-starts on fill, so "x/N" is a real
  // progress bar toward the start. A standard's declaredSize is only a MAX
  // (byes fill the rest), so the same string means something weaker there.
  const seats = `${tournament.participantCount}/${tournament.declaredSize}`;

  return (
    <Link
      to={`/duels/tournaments/${encodeURIComponent(tournament.tournamentId)}`}
      className={cn(
        'flex items-center gap-2.5 rounded-xl bg-bg-secondary p-2.5',
        'transition-opacity active:opacity-80',
        className,
      )}
    >
      {/* Content-height row: the icon chip is sized to sit ~level with the two
          text lines, so the row is driven by its content instead of a tall
          image slot. QDuels tournaments have no poster, so the chip carries a
          brand-tinted gradient with the kind icon — the same for both kinds, so
          the list reads as one family and the icon (⚡ Sit&Go / 🏆 standard)
          still tells them apart. Matched to the Laravel card's 44×44 slot. */}
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand/20 to-brand/5 text-brand">
        {isSitngo ? (
          <Zap aria-hidden className="h-5 w-5" />
        ) : (
          <Trophy aria-hidden className="h-5 w-5" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Title line carries the status badge (as the Laravel card does), so
            state is scannable at the row's top edge instead of buried mid-meta. */}
        <div className="flex items-center gap-2">
          <h3 className="line-clamp-1 min-w-0 flex-1 text-sm font-semibold leading-tight text-fg">
            {tournament.name}
          </h3>
          <QduelsTournamentStatusBadge tournament={tournament} className="shrink-0" />
        </div>

        {/* One meta line, never wrapping: clips the least-important trailing
            marker (recurring) before it forces a second line. The source badge
            leads it — the title line stays uniform with the Laravel card. */}
        <div className="flex flex-nowrap items-center gap-2 overflow-hidden text-xs text-fg-muted">
          {showSourceBadge && (
            <span className="shrink-0 rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">
              {t('tournaments.badge.duels', { defaultValue: 'Duels' })}
            </span>
          )}
          <span className="inline-flex shrink-0 items-center gap-1">
            <Users aria-hidden className="h-3 w-3" />
            {seats}
          </span>
          {tournament.bestOf > 1 && <span className="shrink-0">BO{tournament.bestOf}</span>}
          {tournament.prizeQc > 0 && (
            <span className="inline-flex shrink-0 items-center gap-1 text-amber-400">
              <Coins aria-hidden className="h-3 w-3" />
              {tournament.prizeQc} QC
            </span>
          )}
          {tournament.recurring && (
            <span
              className="inline-flex shrink-0 items-center gap-1"
              title={t('tournaments.recurring', { defaultValue: 'Recurring' })}
            >
              <Repeat aria-hidden className="h-3 w-3" />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * QDuels has its own status vocabulary (`registration` | `in_progress` |
 * `complete` | `cancelled`) — nothing to do with Laravel's TOURNAMENT_STATUS,
 * hence a local badge rather than reusing `<StatusBadge />`.
 */
export function QduelsTournamentStatusBadge({
  tournament,
  className,
}: {
  tournament: QduelsTournamentSummary;
  className?: string;
}) {
  const { t } = useTranslation('duels');

  const map: Record<string, { label: string; cls: string }> = {
    registration: {
      label: t('tournaments.status.registration', { defaultValue: 'Open' }),
      cls: 'bg-success/15 text-success',
    },
    in_progress: {
      label: t('tournaments.status.inProgress', { defaultValue: 'Live' }),
      cls: 'bg-brand/15 text-brand',
    },
    complete: {
      label: t('tournaments.status.complete', { defaultValue: 'Finished' }),
      cls: 'bg-bg-tertiary text-fg-muted',
    },
    cancelled: {
      label: t('tournaments.status.cancelled', { defaultValue: 'Cancelled' }),
      cls: 'bg-danger/15 text-danger',
    },
  };
  const s = map[tournament.status] ?? map.registration;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
        s.cls,
        className,
      )}
    >
      {s.label}
    </span>
  );
}
